import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, ButtonBase, Chip, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    KeyboardArrowUp as CollapseIcon,
    KeyboardArrowDown as ExpandIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { isLocationMode } from './utils/stepTwoUtils';
import { STATUS_FINISH, CSEP_STORE, LTT_STORE, FALL_ARREST_RESCUE_PLAN_STORE } from '../../config/constants';
import { initializeDB } from '../../config/db';
import { generateJhaPdfBlob } from './pdf/generateJhaPdf';
import PdfPreviewModal from './pdf/PdfPreviewModal';
import { TENANT_LOGO_URL } from '../../config/tenant';
import { findMissingDocumentsLocal } from './utils/documentTriggerEvaluator';
import { localDateString } from '../../utils/localDate';

// Подгружает из IDB полные данные привязанных форм (FARP/CSEP/LTT) по их uuid,
// чтобы встроить их превью прямо в JHA PDF. Полностью офлайн (читает IndexedDB).
async function loadLinkedForms(jhaData) {
    const out = {};
    try {
        const docs = [
            ...(jhaData?.stepTwo?.docsPermits || []),
            ...(jhaData?.stepTwo?.customDocsPermits || []),
        ];
        const farpIds = docs.map(d => d.linkedFarpUuid).filter(Boolean);
        const csepIds = docs.map(d => d.linkedCsepUuid).filter(Boolean);
        const lttIds  = docs.map(d => d.linkedLttUuid).filter(Boolean);
        if (!farpIds.length && !csepIds.length && !lttIds.length) return out;

        const db = await initializeDB();
        const fetchInto = async (storeName, ids) => {
            if (!ids.length || !db.objectStoreNames.contains(storeName)) return;
            for (const uuid of ids) {
                try {
                    const rec = await db.get(storeName, uuid);
                    if (rec) out[uuid] = rec;
                } catch { /* ignore single miss */ }
            }
        };
        await fetchInto(FALL_ARREST_RESCUE_PLAN_STORE, farpIds);
        await fetchInto(CSEP_STORE, csepIds);
        await fetchInto(LTT_STORE, lttIds);
    } catch (e) {
        console.warn('[JhaStepHeader] loadLinkedForms failed', e);
    }
    return out;
}

// Steps swapped: position 2 = Work Activities, position 3 = Documents.
const STEP_CONFIG = [
    { number: 1, title: 'General', path: '/jha/step-1' },
    { number: 2, title: 'Activity', path: '/jha/step-2' },
    { number: 3, title: 'Document', path: '/jha/step-3' },
    { number: 4, title: 'Crew', path: '/jha/step-4' },
    { number: 5, title: 'Review', path: '/jha/step-5' }
];

const STEP_STATE_STYLES = {
    complete: {
        label: 'Complete',
        background: '#f6faf6',
        border: '#c5e1c6',
        accent: '#2e7d32',
        circleBg: '#2e7d32',
        circleColor: '#ffffff'
    },
    incomplete: {
        label: 'Incomplete',
        background: '#fff8e1',
        border: '#ffcc80',
        accent: '#f57c00',
        circleBg: '#f57c00',
        circleColor: '#ffffff'
    },
    active: {
        label: 'Current step',
        background: '#eef4fc',
        border: '#b3d4fc',
        accent: '#1565c0',
        circleBg: '#1565c0',
        circleColor: '#ffffff'
    },
    upcoming: {
        label: 'Next',
        background: '#ffffff',
        border: '#d0d7de',
        accent: '#90a4ae',
        circleBg: '#eceff1',
        circleColor: '#546e7a'
    },
    locked: {
        label: 'Locked',
        background: '#fafafa',
        border: '#e0e0e0',
        accent: '#b0bec5',
        circleBg: '#f0f1f2',
        circleColor: '#b0bec5'
    }
};

const STATUS_BADGES = {
    draft: { label: 'Draft', bg: '#fff3e0', border: '#ffcc80', color: '#e65100' },
    saved: { label: 'Saved', bg: '#e3f2fd', border: '#90caf9', color: '#0d47a1' },
    process: { label: 'In Process', bg: '#fff8e1', border: '#ffe082', color: '#f57c00' },
    submitted: { label: 'Submitted', bg: '#e8f4ff', border: '#bbdefb', color: '#1565c0' },
    finish: { label: 'Completed', bg: '#e8f5e9', border: '#a5d6a7', color: '#2e7d32' }
};

export default function JhaStepHeader({
    currentStep,
    jhaData,
    disableNavigation = false,
    fromViewer = false
}) {
    const navigate = useNavigate();
    const [navCollapsed, setNavCollapsed] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);

    // Handle Preview PDF - generates blob and opens modal
    const handlePreviewPdf = async () => {
        if (generatingPdf || !jhaData) return;

        setGeneratingPdf(true);
        setPdfModalOpen(true); // Open modal immediately to show loading state
        setPdfBlob(null);

        try {
            // Debug: log jhaData structure
            console.log('JHA Data for PDF:', {
                stepFour: jhaData.stepFour,
                stepFive: jhaData.stepFive,
                status: jhaData.status,
                submittedAt: jhaData.submittedAt,
                preShiftSupervisorSignature: jhaData.preShiftSupervisorSignature,
                postShiftSupervisorSignature: jhaData.postShiftSupervisorSignature
            });

            // Get crew members from multiple possible sources
            const acknowledgments = jhaData.stepFour?.employeeAcknowledgments || jhaData.employeeAcknowledgments || [];
            const selectedEmployees = jhaData.stepFour?.employeesSelected || jhaData.crewMembers || [];

            let crewMembers = [];
            if (acknowledgments.length > 0) {
                crewMembers = acknowledgments.map(ack => ({
                    fullName: ack.employeeName || ack.fullName || ack.FullName || ack.name || '',
                    signature: ack.signature || null
                }));
            } else if (selectedEmployees.length > 0) {
                crewMembers = selectedEmployees.map(emp => ({
                    fullName: emp.FullName || emp.fullName || emp.employeeName || emp.name || '',
                    signature: emp.signature || null
                }));
            }

            console.log('Crew members for PDF:', crewMembers);

            // Load linked forms (FARP/CSEP/LTT) from IDB so their preview can be
            // embedded inline into the JHA PDF (offline-safe).
            const linkedForms = await loadLinkedForms(jhaData);

            // Generate PDF blob for in-app preview (iPad friendly)
            const blob = await generateJhaPdfBlob(jhaData, crewMembers, {
                logoUrl: TENANT_LOGO_URL,
                energyWheelUrl: '/images/energy_wheel.png',
                linkedForms
            });

            setPdfBlob(blob);
        } catch (error) {
            console.error('Failed to generate PDF:', error);
            alert('Failed to generate PDF preview. Please try again.');
            setPdfModalOpen(false);
        } finally {
            setGeneratingPdf(false);
        }
    };

    // Close PDF modal
    const handleClosePdfModal = () => {
        setPdfModalOpen(false);
        setPdfBlob(null);
    };

    // Generate filename for download
    const getPdfFilename = () => {
        const stepOne = jhaData?.stepOne || {};
        const jobNumber = stepOne.jobNumber || 'JHA';
        const date = stepOne.date || localDateString();
        return `JHA_${jobNumber}_${date}.pdf`;
    };
    const currentStepConfig = useMemo(
        () => STEP_CONFIG.find(step => step.number === currentStep),
        [currentStep]
    );
    const jobIdentifier = useMemo(() => {
        const stepOne = jhaData?.stepOne || {};
        return stepOne.jobSiteName || stepOne.jobNumber || stepOne.locationName || 'New JHA';
    }, [jhaData]);
    const normalizedStatus = useMemo(() => {
        const rawStatus = jhaData?.status === 'completed' ? STATUS_FINISH : jhaData?.status;
        return rawStatus || 'draft';
    }, [jhaData]);
    const statusBadge = STATUS_BADGES[normalizedStatus] || {
        label: 'In Progress',
        bg: '#e3f2fd',
        border: '#bbdefb',
        color: '#1565c0'
    };

    // Trigger-based missing required documents (async, reuses the shared evaluator —
    // accounts for submitted FARP + linkedFarpUuid). Surfaced on the Document tab.
    const [triggerMissingCount, setTriggerMissingCount] = useState(0);
    useEffect(() => {
        if (!jhaData) { setTriggerMissingCount(0); return; }
        let cancelled = false;
        (async () => {
            try {
                const missing = await findMissingDocumentsLocal(jhaData);
                if (!cancelled) setTriggerMissingCount(Array.isArray(missing) ? missing.length : 0);
            } catch (e) {
                if (!cancelled) setTriggerMissingCount(0);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jhaData?.stepTwo, jhaData?.stepThree, jhaData?.localId]);

    const stepStatuses = useMemo(() => {
        const stepOne = jhaData?.stepOne || {};
        const stepTwo = jhaData?.stepTwo || {};
        const stepThree = jhaData?.stepThree || {};
        const stepFour = jhaData?.stepFour || {};

        // Step 1: General info + Evaluation
        const generalComplete = (() => {
            const hasSupervisor = Boolean(stepOne.jobSiteSupervisor);
            const hasDate = Boolean(stepOne.date);
            if (!hasSupervisor || !hasDate) return false;
            if (isLocationMode(stepOne.mode)) {
                return Boolean((stepOne.locationName || '').trim());
            }
            return Boolean(stepOne.jobNumber);
        })();
        const evaluationComplete = (stepOne.evaluationOfWork || []).every(item =>
            item.selected === 'YES' || item.selected === 'N/A'
        );
        const step1Complete = generalComplete && evaluationComplete;

        const initialStatus = stepThree.initialSubmission?.status;
        const initialReportSent = initialStatus === 'sent' || initialStatus === 'pending';

        // Position 2: Work Activities + Energy
        const energyRows = stepThree.energyExposures || [];
        const hasSelection = energyRows.some(row =>
            Array.isArray(row.energySelections) && row.energySelections.some(sel => sel.energyId)
        );
        const allSelectionsHaveHazards = energyRows.every(row =>
            (row.energySelections || []).every(sel => !sel.energyId || (sel.hazards && sel.hazards.length))
        );
        const allActiveRowsHaveActivity = energyRows.every(row => {
            const hasEnergy = Array.isArray(row.energySelections) && row.energySelections.some(sel => sel.energyId);
            if (!hasEnergy) return true;
            return Boolean(row.workActivity && row.workActivity.trim());
        });
        const activityComplete = hasSelection && allSelectionsHaveHazards && allActiveRowsHaveActivity;

        // Position 3: Documents (+ initial submission lives here now)
        const allDocs = [
            ...(stepTwo.docsPermits || []),
            ...(stepTwo.customDocsPermits || [])
        ];
        const allDocsHaveRequirement = allDocs.length === 0 || allDocs.every(doc => doc.required);
        // Используем АВТОРИТЕТНЫЙ async-счётчик (findMissingDocumentsLocal): он учитывает
        // attachment / linkedCsep / linkedLtt / linkedFarp И submitted FARP. Раньше тут
        // был Math.max c синхронной проверкой, из-за чего поданный/привязанный FARP всё
        // равно показывался как "missing" в табе (баг "не писалось что подтянулось").
        const docMissingCount = triggerMissingCount;
        const documentComplete = allDocsHaveRequirement && docMissingCount === 0 && initialReportSent;

        // Step 4: Crew selection - complete only when ALL selected employees have signed
        const selectedEmployees = stepFour.employeesSelected || [];
        const acknowledgments = stepFour.employeeAcknowledgments || [];
        const signedCount = acknowledgments.filter(ack => ack.signature && ack.signedAt).length;
        const step4Complete = selectedEmployees.length > 0 && signedCount === selectedEmployees.length;

        // Step 5: Final review
        const step5Complete = jhaData?.status === STATUS_FINISH;

        return {
            1: { complete: step1Complete },
            2: { complete: activityComplete },
            3: { complete: documentComplete, missingDocs: docMissingCount, hasRequirements: allDocsHaveRequirement },
            4: { complete: step4Complete },
            5: { complete: step5Complete }
        };
    }, [jhaData, triggerMissingCount]);

    const metaLastStep = jhaData?.meta?.lastStep || 1;
    const initialStatus = jhaData?.stepThree?.initialSubmission?.status;
    const initialSent = initialStatus === 'sent' || initialStatus === 'pending';
    // Crew complete = all selected employees have signed
    const selectedEmployeesCount = (jhaData?.stepFour?.employeesSelected || []).length;
    const signedAcknowledgmentsCount = (jhaData?.stepFour?.employeeAcknowledgments || []).filter(ack => ack.signature && ack.signedAt).length;
    const crewComplete = selectedEmployeesCount > 0 && signedAcknowledgmentsCount === selectedEmployeesCount;

    const canAccessStep = (stepNumber) => {
        if (disableNavigation && stepNumber !== currentStep) {
            return false;
        }

        if (fromViewer) {
            return true;
        }

        if (stepNumber <= metaLastStep) {
            return true;
        }

        switch (stepNumber) {
            case 1:
                return true;
            case 2:
                // Activities — needs General complete
                return Boolean(stepStatuses[1]?.complete);
            case 3:
                // Documents — needs General + Activities complete
                return Boolean(stepStatuses[1]?.complete) && Boolean(stepStatuses[2]?.complete);
            case 4:
                return initialSent;
            case 5:
                return initialSent && crewComplete;
            default:
                return false;
        }
    };

    const handleStepClick = (stepNumber) => {
        if (stepNumber === currentStep) return;
        if (!canAccessStep(stepNumber)) return;
        const config = STEP_CONFIG.find(step => step.number === stepNumber);
        if (!config) return;
        navigate(config.path, { state: { jha: jhaData, isNew: false, viewMode: fromViewer, fromViewer } });
    };

    const handleBack = () => {
        navigate('/jha');
    };

    const renderStepPill = (config) => {
        const status = stepStatuses[config.number] || {};
        const isActive = config.number === currentStep;
        const isComplete = Boolean(status.complete);
        const isOptional = Boolean(config.optional);
        const accessible = canAccessStep(config.number);

        // Document tab (position 3) — show incomplete with a count if required
        // documents are still missing a file/linked form (incl. FARP/triggered docs).
        const isDocStepIncomplete = config.number === 3 &&
                                    !status.complete &&
                                    (status.missingDocs || 0) > 0;

        let state = 'upcoming';
        if (!accessible) state = 'locked';
        if (isComplete) state = 'complete';
        if (isDocStepIncomplete) state = 'incomplete';
        if (isActive) state = 'active';

        const stateStyles = STEP_STATE_STYLES[state];

        let helperLabelRaw = isOptional && state !== 'complete' ? 'Optional' : stateStyles.label;
        if (isDocStepIncomplete) {
            helperLabelRaw = `${status.missingDocs} doc${status.missingDocs > 1 ? 's' : ''} missing`;
        }
        const helperLabel = state === 'upcoming' ? '' : helperLabelRaw;

        return (
            <ButtonBase
                key={config.number}
                onClick={() => handleStepClick(config.number)}
                disabled={!accessible}
                sx={{
                    width: '100%',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    borderRadius: 1,
                    backgroundColor: stateStyles.background,
                    border: `1px solid ${stateStyles.border}`,
                    color: state === 'locked' ? '#9e9e9e' : '#263238',
                    px: 1.1,
                    py: 0.65,
                    minHeight: 48,
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        backgroundColor: accessible ? stateStyles.background : undefined,
                        borderColor: accessible ? stateStyles.accent : stateStyles.border
                    },
                    '& .MuiTypography-root': {
                        color: 'inherit'
                    },
                    '&.Mui-disabled': {
                        opacity: 1,
                        color: '#9e9e9e',
                        backgroundColor: '#f7f7f7',
                        borderColor: '#e0e0e0'
                    }
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            backgroundColor: stateStyles.circleBg,
                            color: stateStyles.circleColor,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {config.number}
                    </Box>
                    <Stack spacing={0.25} sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '0.95rem' }, color: state === 'locked' ? '#9e9e9e' : 'inherit' }}>
                            {config.title}
                        </Typography>
                        {helperLabel && (
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 500, color: state === 'locked' ? '#b0bec5' : stateStyles.accent }}
                            >
                                {helperLabel}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </ButtonBase>
        );
    };

    const ToggleIcon = navCollapsed ? ExpandIcon : CollapseIcon;

    return (
        <Box
            data-jha-header
            sx={{
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #e0e0e0',
                px: { xs: 1.25, sm: 1.75 },
                py: { xs: 1.1, sm: 1.3 },
                position: 'sticky',
                top: 0,
                zIndex: 10
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={{ xs: 0.9, sm: 1.2 }}
            >
                <Stack direction="row" spacing={0.8} alignItems="center">
                    <IconButton
                        onClick={handleBack}
                        size="small"
                        title="Back to JHA list"
                        aria-label="Back to JHA list"
                        sx={{
                            border: '1px solid #d0d7de',
                            borderRadius: 1.5,
                            backgroundColor: 'transparent',
                            width: 36,
                            height: 36,
                            '&:hover': {
                                backgroundColor: '#eef4fc',
                                borderColor: '#90caf9'
                            }
                        }}
                    >
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Stack spacing={0.3}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#263238' }}>
                            {jobIdentifier}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#607d8b', letterSpacing: 0.2 }}>
                            {`#${jhaData?.id || 'New'} · Step ${currentStep} of 5${currentStepConfig ? ` · ${currentStepConfig.title}` : ''}`}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={0.6} alignItems="center">
                    {/* Preview PDF button - shows after initial report sent (step 3+) */}
                    {initialSent && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handlePreviewPdf}
                            disabled={generatingPdf}
                            startIcon={generatingPdf ? <CircularProgress size={14} /> : <PdfIcon />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                borderColor: '#d32f2f',
                                color: '#d32f2f',
                                backgroundColor: '#fff',
                                px: 1.5,
                                py: 0.5,
                                minHeight: 32,
                                '&:hover': {
                                    backgroundColor: '#ffebee',
                                    borderColor: '#c62828'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#f5f5f5',
                                    borderColor: '#e0e0e0',
                                    color: '#9e9e9e'
                                }
                            }}
                        >
                            Preview PDF
                        </Button>
                    )}
                    <Chip
                        label={statusBadge.label}
                        size="small"
                        variant="outlined"
                        sx={{
                            fontWeight: 600,
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                            borderColor: statusBadge.border,
                            color: statusBadge.color,
                            height: 28
                        }}
                    />
                    <IconButton
                        size="small"
                        onClick={() => setNavCollapsed(prev => !prev)}
                        aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                    >
                        <ToggleIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Stack>

            {!navCollapsed && (
                <>
                    <Divider sx={{ my: { xs: 0.8, sm: 1 } }} />
                    <Box
                        sx={{
                            display: 'grid',
                            gap: { xs: 0.5, sm: 0.65 },
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                sm: 'repeat(5, minmax(0, 1fr))'
                            }
                        }}
                    >
                        {STEP_CONFIG.map(renderStepPill)}
                    </Box>
                </>
            )}

            {/* PDF Preview Modal - stays in app context */}
            <PdfPreviewModal
                open={pdfModalOpen}
                onClose={handleClosePdfModal}
                pdfBlob={pdfBlob}
                filename={getPdfFilename()}
                loading={generatingPdf}
            />
        </Box>
    );
}
